import { ReactNode, useMemo, useState } from "react";

import classNames from "../../../utils/classNames";

export type StatusTabState = "complete" | "incomplete";

export type StatusTabItem = {
  key: string;
  title: string;
  status: StatusTabState;
  children: ReactNode;
};

const statusStyles: Record<StatusTabState, { active: string; inactive: string; indicator: string }> = {
  complete: {
    active: "bg-white text-green-700 border-green-200 shadow-sm",
    inactive: "bg-transparent text-green-600 border-transparent hover:bg-green-50",
    indicator: "bg-green-500",
  },
  incomplete: {
    active: "bg-white text-amber-700 border-amber-300 shadow-sm",
    inactive: "bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100",
    indicator: "bg-amber-400",
  },
};

export default function StatusTabs({
  tabs,
  defaultTabKey,
  onTabChange,
}: {
  tabs: StatusTabItem[];
  defaultTabKey?: string;
  onTabChange?: (tabKey: string) => void;
}) {
  const fallbackTabKey = useMemo(() => tabs[0]?.key ?? "", [tabs]);
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey ?? fallbackTabKey);

  const activeTab = tabs.find((tab) => tab.key === activeTabKey) ?? tabs[0];

  if (!activeTab) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50">
        <nav className="flex gap-1 px-3 pt-2" aria-label="Lead details tabs">
          {tabs.map((tab) => {
            const isActive = tab.key === activeTab.key;
            const styles = statusStyles[tab.status];

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTabKey(tab.key);
                  onTabChange?.(tab.key);
                }}
                className={classNames(
                  "whitespace-nowrap rounded-t-md border border-b-0 py-2 px-4 text-sm font-medium transition-all -mb-px flex items-center gap-2",
                  isActive ? styles.active : styles.inactive
                )}
              >
                <span>{tab.title}</span>
                <span className={classNames("h-2 w-2 rounded-full shrink-0", styles.indicator)} />
              </button>
            );
          })}
        </nav>
      </div>

      <div>{activeTab.children}</div>
    </div>
  );
}
